import React, { useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Download, FileDown, Loader2 } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Reveal } from "../../lib/motion";

export default function LeadMagnet() {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState(null);
    const reduce = useReducedMotion();

    async function onSubmit(e) {
        e.preventDefault();
        if (!email.trim()) {
            toast.error("We need an email to send it.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/lead-magnet", {
                name: name.trim() || null,
                email: email.trim(),
            });
            const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.download_url}`;
            setDownloadUrl(fullUrl);
            toast.success("Here's your checklist!");
            window.open(fullUrl, "_blank", "noopener");
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err.response?.data?.detail) ||
                    "Could not generate your download. Try again.",
            );
        } finally {
            setLoading(false);
        }
    }

    return (
        <section
            id="playbook"
            className="relative overflow-hidden border-b border-hairline bg-page py-24 text-ink lg:py-36"
            data-testid="lead-magnet-section"
        >
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute -top-24 -right-20 h-[380px] w-[380px] animate-blob rounded-full bg-coral/35 blur-3xl" />
                <div
                    className="absolute -bottom-24 -left-14 h-[300px] w-[300px] animate-blob rounded-full bg-sage/40 blur-3xl"
                    style={{ animationDelay: "-8s" }}
                />
            </div>

            <div className="relative mx-auto max-w-[1400px] px-5 lg:px-10">
                <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-20">
                    {/* Mock cover with subtle floating motion */}
                    <div className="order-2 lg:order-1 lg:col-span-5">
                        <motion.div
                            initial={reduce ? false : { rotate: -3, y: 20, opacity: 0 }}
                            whileInView={reduce ? undefined : { rotate: -3, y: 0, opacity: 1 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            whileHover={reduce ? undefined : { rotate: 0, y: -6 }}
                            className="relative mx-auto aspect-[3/4] max-w-sm border-2 border-ink bg-white p-8 text-ink frame-coral"
                        >
                            <div className="absolute left-0 top-0 h-2 w-full bg-coral" />
                            <div className="mt-6 text-[10px] font-bold uppercase tracking-[0.3em] text-coral">
                                Simple Digital
                            </div>
                            <div className="mt-4 font-display text-3xl font-black leading-[0.95] tracking-tight">
                                The 7-Day Get-Found-Online Checklist
                            </div>
                            <div className="mt-3 font-serif text-base italic text-ink/70">
                                One small task a day. By Sunday, you&apos;re live.
                            </div>
                            <div className="mt-6 space-y-2 text-xs text-ink/70">
                                <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 bg-coral" /> Day 1 — Get clear on who you serve</div>
                                <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 bg-coral" /> Day 2 — Sort your profile basics</div>
                                <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 bg-coral" /> Day 3 — Shoot a month of content</div>
                                <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 bg-coral" /> Day 5 — Publish + engage</div>
                                <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 bg-coral" /> Day 7 — Lock in the rhythm</div>
                            </div>
                            <div className="absolute bottom-6 left-8 right-8 flex items-center justify-between text-[10px] uppercase tracking-widest text-ink/55">
                                <span>v1.0</span>
                                <span>Free · PDF</span>
                            </div>
                        </motion.div>
                    </div>

                    <div className="order-1 lg:order-2 lg:col-span-7">
                        <Reveal>
                            <div className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-coral">
                                // Chapter 05 · Free download
                            </div>
                            <h2 className="font-display text-5xl font-black leading-[0.95] tracking-tight text-ink sm:text-6xl lg:text-7xl">
                                Steal our{" "}
                                <span className="font-serif italic font-normal text-coral">
                                    7-day checklist.
                                </span>
                            </h2>
                            <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/80 lg:text-lg">
                                One small task a day, 20–40 minutes each. By next Sunday you&apos;ll
                                have a clearer profile, a sharper bio, three pieces of content
                                live, and a weekly rhythm you can actually keep. No upsell. No
                                spam. Just a useful PDF.
                            </p>

                            {downloadUrl ? (
                                <div
                                    className="mt-10 flex flex-col items-start gap-4 border-2 border-coral bg-white/5 p-6"
                                    data-testid="lead-magnet-success"
                                >
                                    <div className="font-display text-2xl font-black uppercase text-coral">
                                        Sent &amp; downloading
                                    </div>
                                    <p className="text-sm text-ink/80">
                                        Didn&apos;t open? Click below to download manually.
                                    </p>
                                    <a
                                        href={downloadUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 border-2 border-ink px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-ink hover:bg-white hover:text-ink"
                                        data-testid="lead-magnet-download-link"
                                    >
                                        <Download className="h-4 w-4" /> Download checklist (PDF)
                                    </a>
                                </div>
                            ) : (
                                <form
                                    onSubmit={onSubmit}
                                    className="mt-10 grid gap-3 sm:grid-cols-[1fr_1fr_auto]"
                                    data-testid="lead-magnet-form"
                                >
                                    <input
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Your name (optional)"
                                        className="border-2 border-ink/30 bg-transparent px-4 py-3 text-ink placeholder:text-ink/45 focus:border-coral focus:outline-none"
                                        data-testid="lead-magnet-name"
                                    />
                                    <input
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        type="email"
                                        required
                                        placeholder="you@yourshop.co"
                                        className="border-2 border-ink/30 bg-transparent px-4 py-3 text-ink placeholder:text-ink/45 focus:border-coral focus:outline-none"
                                        data-testid="lead-magnet-email"
                                    />
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="inline-flex items-center justify-center gap-2 border-2 border-coral bg-coral px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-ink transition-all hover:-translate-y-0.5 hover:bg-coral-hover disabled:opacity-60"
                                        data-testid="lead-magnet-submit"
                                    >
                                        {loading ? (
                                            <>
                                                Sending…
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            </>
                                        ) : (
                                            <>
                                                Send checklist <FileDown className="h-4 w-4" />
                                            </>
                                        )}
                                    </button>
                                </form>
                            )}

                            <div className="mt-5 text-xs uppercase tracking-widest text-ink/50">
                                We&apos;ll never sell your email. One-tap unsubscribe always.
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}
